import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Lightbulb, ArrowRight, Loader2 } from 'lucide-react';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function InlineSearchAutocomplete({ value, onChange, onPageReset, className = '' }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const navigate = useNavigate();
  const wrapperRef = useRef(null);
  const abortRef = useRef(null);

  // Fetch suggestions
  useEffect(() => {
    if (!value || value.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setLoading(true);
    const timer = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/search/autocomplete?q=${encodeURIComponent(value)}&limit=15`,
          { signal: abortRef.current.signal }
        );
        const data = await res.json();
        setSuggestions(data.results || []);
        setShowDropdown(data.results?.length > 0);
        setActiveIdx(-1);
      } catch (e) {
        if (e.name !== 'AbortError') setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [value]);

  // Close on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const goTo = (url) => {
    setShowDropdown(false);
    navigate(url);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      goTo(suggestions[activeIdx].url);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const formatCollection = (col) => {
    if (!col) return '';
    return col.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
      <Input
        placeholder="Search by family, article code, name..."
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (onPageReset) onPageReset();
        }}
        onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
        onKeyDown={handleKeyDown}
        className="pl-10 h-9 text-sm pr-8"
        data-testid="catalog-search-input"
        autoComplete="off"
      />
      {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground animate-spin" />}

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-[#E5E5E5] z-50 overflow-hidden" data-testid="catalog-search-dropdown">
          {suggestions.map((item, i) => (
            <button
              key={item.canonical_slug || item.base_code}
              onClick={() => goTo(item.url)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                i === activeIdx ? 'bg-[#F5F5F0]' : 'hover:bg-[#FAFAF8]'
              } ${i > 0 ? 'border-t border-[#F0F0F0]' : ''}`}
              data-testid={`catalog-suggestion-${item.base_code}`}
            >
              <div className="w-10 h-10 rounded bg-[#F5F5F5] overflow-hidden shrink-0 border border-[#EAEAEA]">
                {item.primary_image ? (
                  <img src={item.primary_image} alt={item.family} className="w-full h-full object-contain" onError={e => { e.target.style.display = 'none'; }} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Lightbulb className="w-3 h-3 text-[#CCC]" /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-xs text-[#1A1A1A] truncate">{item.family}</span>
                  <span className="text-[10px] text-[#999] font-mono">{item.base_code}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {item.primary_category_name && <span className="text-[10px] text-[#666]">{item.primary_category_name}</span>}
                  {item.primary_collection && (
                    <span className="text-[9px] uppercase tracking-wider text-[#999] bg-[#F0F0F0] px-1 py-0.5 rounded">
                      {formatCollection(item.primary_collection)}
                    </span>
                  )}
                </div>
              </div>
              <ArrowRight className="w-3 h-3 text-[#CCC] shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
